<template>
  <a-radio-group :value="selectedValue" :disabled="disabled" @change="handleRadioChange">
    <div class="flex flex-col gap-3">
      <a-radio
        v-for="(option, index) in options"
        :key="`${option?.value}_option_${index}`"
        :value="option?.value"
      >
        <div class="flex flex-col gap-2">
          <div class="flex flex-col">
            <div class="flex flex-row gap-[2px] items-center">
              <span class="text-sm font-semibold">{{ option?.label }}</span>

              <ThemeImage
                v-if="option?.icon"
                class="size-4"
                :icon="option?.icon"
                :alt="`${option?.value?.toLowerCase()}_icon`"
              />
            </div>

            <span class="text-xs font-normal secondary_color">{{
              option?.description
            }}</span>
          </div>

          <div v-if="option?.component && selectedValue === option?.value">
            <component
              :is="option.component"
              v-bind="getComponentProps(option)"
              v-model="componentModelValue"
              @selection-change="handleComponentSelectionChange"
            />
            <div
              v-if="showSelectionCount && selectedCountNumbers"
              class="flex items-center gap-1 mt-2 px-2"
            >
              <img
                class="size-4"
                :src="imagePath.neutral.grey.infoIconGrey"
                alt="info_icon"
              />
              <span class="text-xs secondary_color">
                <span class="font-semibold">{{ selectedCountNumbers }}</span>
                <span> have access</span>
              </span>
            </div>
          </div>
        </div>
      </a-radio>
    </div>
  </a-radio-group>
</template>

<script setup>
import { ref, computed } from "vue";
import imagePath from "@/src/paths/imagePath";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  selectedValue: { type: String, required: true },
  options: { type: Array, required: true },
  componentProps: { type: Object, default: () => ({}) },
  showSelectionCount: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:selected-value", "selection-change"]);

const componentModelValue = ref([]);
const internalSelectionByTab = ref({ users: [], teams: [] });

const selectedCountNumbers = computed(() => {
  const { users = [], teams = [] } = internalSelectionByTab.value;

  const counts = [
    { count: users.length, label: "User" },
    { count: teams.length, label: "Team" },
  ];

  const parts = counts
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}${count > 1 ? "s" : ""}`);

  return parts.length ? parts.join(", ") : "";
});

const getComponentProps = (option) => {
  try {
    const baseProps = { ...props.componentProps };

    if (option.componentProps) {
      Object.assign(baseProps, option.componentProps);
    }

    return baseProps;
  } catch (error) {
    console.error("An error occurred while getting component props: ", error);
  }
};

const handleRadioChange = (event) => {
  try {
    const newValue = event.target.value;
    emit("update:selected-value", newValue);
  } catch (error) {
    console.error("An error occurred while handling radio change: ", error);
  }
};

const handleComponentSelectionChange = (data) => {
  try {
    if (data.byTab) {
      internalSelectionByTab.value = data.byTab;
    }

    emit("selection-change", {
      ...data,
      entities: componentModelValue.value,
    });
  } catch (error) {
    console.error(
      "An error occurred while handling component selection change: ",
      error,
    );
  }
};
</script>

<style scoped>
:deep(.ant-radio-wrapper) {
  align-items: center !important;
}

:deep(.ant-radio-wrapper .ant-radio) {
  align-self: flex-start;
  margin-top: 2px;
}
</style>
